import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Form,
  InputGroup,
  Spinner,
  Stack,
} from "react-bootstrap";
import SuccessButton from "../../organisms/SuccessButton";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  ListaClientes,
  ModificacionesPlantilla,
  PERIODICIDADES_CORREOS_MASIVOS,
  PeriodicidadCorreoMasivo,
  QUERY_LEER_PLANTILLA,
  QUERY_LEER_PLANTILLAS,
  descargarAdjuntoPlantilla,
  descargarCuerpoGrafico,
  editarPlantilla,
  eliminarPlantilla,
  enviarPlantilla,
  labelPorPeriodicidadCorreoMasivo,
  useLeerPlantilla,
} from "../../../services/correosMasivosServicios";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { prepararCuerpoParaEnvio } from "./utils";
import LoadingScreen from "../../organisms/LoadingScreen";
import { RespuestaArchivo } from "../../../utils/fileUtils";
import { convertBase64ToBlob } from "../../../utils/blobUtils";
import { downloadBlob } from "../../../utils/downloadUtils";
import {
  RespuestaLeerPropietarios,
  useLeerPropietarios,
} from "../../../services/propietariosServicios";
import PropietariosModal from "./PropietariosModal";
import { PropietarioIndividual } from "./PropietarioIndividual";
import { useNavigate } from "react-router-dom";
import { RUTA_COMUNICACIONES_CORREOS_MASIVOS } from "../../../services/rutas";

export const Plantilla = ({
  idPlantilla,
  listasClientes,
}: {
  idPlantilla: string;
  listasClientes: ListaClientes[];
}) => {
  const [cuerpo, setCuerpo] = useState<string | undefined>("");
  const [cuerpoFueTocado, setCuerpoFueTocado] = useState<boolean>(false);

  const [usarCuerpoGrafico, setUsarCuerpoGrafico] = useState<boolean>(false);
  // Para edicion de plantilla solamente
  const [cuerpoGrafico, setCuerpoGrafico] = useState<File | undefined>(
    undefined
  );
  const [cuerpoGraficoFueTocado, setCuerpoGraficoFueTocado] =
    useState<boolean>(false);

  const [asunto, setAsunto] = useState<string | undefined>("");
  const [asuntoFueTocado, setAsuntoFueTocado] = useState<boolean>(false);

  const [usarGruposDePropietarios, setUsarGruposDePropietarios] =
    useState<boolean>(true);

  const [grupoPropietarios, setGrupoPropietarios] = useState<
    string | undefined
  >("");
  const [grupoPropietariosFueTocado, setGrupoPropietariosFueTocado] =
    useState<boolean>(false);

  const [propietariosIndividuales, setPropietariosIndividuales] = useState<
    Array<RespuestaLeerPropietarios>
  >([]);
  const [
    propietariosIndividualesFueTocado,
    setPropietariosIndividualesFueTocado,
  ] = useState<boolean>(false);

  const [programar, setProgramar] = useState<boolean>(false);
  const [programarFueTocado, setProgramarFueTocado] = useState<boolean>(false);

  const [periodicidad, setPeriodicidad] = useState<
    PeriodicidadCorreoMasivo | undefined
  >(undefined);

  const [periodicidadFueTocada, setPeriodicidadFueTocada] =
    useState<boolean>(false);

  const [eliminarAdjunto, setEliminarAdjunto] = useState<boolean | undefined>(
    undefined
  );

  const [errorDescargaAdjunto, setErrorDescargaAdjunto] =
    useState<boolean>(false);

  const [errorDescargaCuerpoGrafico, setErrorDescargaCuerpoGrafico] =
    useState<boolean>(false);

  const [mostrarPropietariosModal, setMostrarPropietariosModal] =
    useState<boolean>(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const huboModificaciones =
    grupoPropietariosFueTocado ||
    propietariosIndividualesFueTocado ||
    cuerpoGraficoFueTocado ||
    asuntoFueTocado ||
    programarFueTocado ||
    periodicidadFueTocada ||
    cuerpoFueTocado ||
    eliminarAdjunto;

  const {
    data: plantilla,
    isError: plantillaIsError,
    isSuccess: plantillaIsSuccess,
    isLoading: plantillaIsLoading,
  } = useLeerPlantilla(idPlantilla);

  const {
    data: propietarios,
    isError: propietariosIsError,
    isSuccess: propietariosIsSuccess,
    isLoading: propietariosIsLoading,
  } = useLeerPropietarios();

  const editarPlantillaMutation = useMutation(editarPlantilla, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_LEER_PLANTILLA, idPlantilla]);
    },
  });

  const eliminarPlantillaMutation = useMutation(eliminarPlantilla, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_LEER_PLANTILLAS]);
      navigate(RUTA_COMUNICACIONES_CORREOS_MASIVOS);
    },
  });

  const enviarMensajeMutation = useMutation(enviarPlantilla);

  const mutacionesIsLoading = 
    editarPlantillaMutation.isLoading || 
    eliminarPlantillaMutation.isLoading || 
    enviarMensajeMutation.isLoading;

  const propietariosPorSeleccionar = useMemo(() => {
    if (propietarios && propietariosIsSuccess) {
      if (propietariosIndividuales) {
        return propietarios.filter(
          (p) => !propietariosIndividuales.some((pi) => pi.id === p.id)
        );
      } else {
        return propietarios || [];
      }
    }
    return [];
  }, [propietarios, propietariosIndividuales, propietariosIsSuccess]);

  const onProgramarToggle = () => {
    if (!programar) {
      setPeriodicidad("DIARIA");
      setPeriodicidadFueTocada(true);
    }
    setProgramar((p) => !p);
    if (!programarFueTocado) {
      setProgramarFueTocado(true);
    }
  };

  const reiniciarFueTocados = () => {
    setAsuntoFueTocado(false);
    setCuerpoFueTocado(false);
    setGrupoPropietariosFueTocado(false);
    setPeriodicidadFueTocada(false);
    setPropietariosIndividualesFueTocado(false);
    setCuerpoGraficoFueTocado(false);
    setProgramarFueTocado(false);
    setPeriodicidadFueTocada(false);
    setEliminarAdjunto(undefined);
  };

  const onGuardarClick = () => {
    let periodicidadVal = undefined;
    if (periodicidadFueTocada) {
      periodicidadVal = periodicidad;
    } else {
      periodicidadVal = undefined;
    }

    const idsPropietariosIndividuales = propietariosIndividuales?.map(
      (p) => p.id
    );
    if (idsPropietariosIndividuales.length === 0 && !grupoPropietarios) {
      throw Error(
        "Grupo de propietarios y propietarios individuales no pueden ser nulos ambos"
      );
    }
    if (idsPropietariosIndividuales.length > 0 && grupoPropietarios) {
      throw Error(
        "Grupo de propietarios y propietarios individuales no pueden estar definidos ambos"
      );
    }

    const ediciones: ModificacionesPlantilla = {
      id: idPlantilla,
      propietariosIndividuales: propietariosIndividualesFueTocado
        ? idsPropietariosIndividuales
        : undefined,
      grupoPropietarios: grupoPropietariosFueTocado
        ? grupoPropietarios
          ? grupoPropietarios
          : null
        : undefined,
      asunto: asuntoFueTocado ? asunto : undefined,
      cuerpo: cuerpoFueTocado
        ? cuerpo
          ? prepararCuerpoParaEnvio(cuerpo)
          : null
        : undefined,
      periodicidad: periodicidadVal,
      eliminarProgramacion: programarFueTocado ? !programar : undefined,
      cuerpoGrafico: cuerpoGraficoFueTocado
        ? cuerpoGrafico
          ? cuerpoGrafico
          : null
        : undefined,
      eliminarAdjunto: eliminarAdjunto,
    };

    editarPlantillaMutation.mutate(ediciones);
  };

  const onEnviarClick = () => {
    enviarMensajeMutation.mutate(idPlantilla);
  };

  const onDescargarAdjuntoClick = async () => {
    let adjunto: RespuestaArchivo | undefined | null;
    setErrorDescargaAdjunto(false);
    try {
      adjunto = await descargarAdjuntoPlantilla(idPlantilla);
    } catch (error: any) {
      setErrorDescargaAdjunto(true);
    }
    if (adjunto) {
      const content = adjunto.contenido;
      const contenidoBlob = convertBase64ToBlob(
        content,
        adjunto.extension || "application/pdf"
      );
      downloadBlob(contenidoBlob, adjunto.nombre || "adjunto_plantilla.png");
    }
  };

  const onEliminarAdjuntoClick = () => {
    setEliminarAdjunto(true);
  };

  const onDescargarCuerpoGraficoClick = async () => {
    let cuerpoGraficoArchivo: RespuestaArchivo | undefined | null;
    setErrorDescargaCuerpoGrafico(false);
    try {
      cuerpoGraficoArchivo = await descargarCuerpoGrafico(idPlantilla);
    } catch (error: any) {
      setErrorDescargaCuerpoGrafico(true);
    }
    if (cuerpoGraficoArchivo) {
      const content = cuerpoGraficoArchivo.contenido;
      const contenidoBlob = convertBase64ToBlob(
        content,
        cuerpoGraficoArchivo.extension || "image/png"
      );
      downloadBlob(
        contenidoBlob,
        cuerpoGraficoArchivo.nombre || "cuerpo_grafico.png"
      );
    }
  };

  const onAgregarPropietarioClick = () => {
    setMostrarPropietariosModal(true);
  };

  const onCerrarPropietariosModal = () => {
    setMostrarPropietariosModal(false);
  };

  const onSeleccionarPropietario = (propietario: RespuestaLeerPropietarios) => {
    setPropietariosIndividualesFueTocado(true);
    if (!propietariosIndividuales) {
      setPropietariosIndividuales([propietario]);
    } else {
      setPropietariosIndividuales((p) => [...p!, propietario]);
    }
  };

  const onSeleccionarCuerpo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      setCuerpoGraficoFueTocado(true);
      setCuerpoGrafico(files[0]);
    } else {
      setCuerpoGrafico(undefined);
    }
  };

  const onUsarCuerpoGraficoToggle = () => {
    setUsarCuerpoGrafico((p) => !p);
    setCuerpo(undefined);
    setCuerpoGrafico(undefined);
    setCuerpoFueTocado(true);
    setCuerpoGraficoFueTocado(true);
  };

  const onEliminarPropietario = (propietario: RespuestaLeerPropietarios) => {
    if (propietariosIndividuales) {
      setPropietariosIndividualesFueTocado(true);
      setPropietariosIndividuales((px) =>
        px.filter((p) => p.id !== propietario.id)
      );
    }
  };

  const onEliminarPlantillaClick = () => {
    eliminarPlantillaMutation.mutate(idPlantilla);
  }

  // Inicializar campos con plantilla descargada
  useEffect(() => {
    if (plantillaIsSuccess && plantilla) {
      if (plantilla.cuerpo) {
        setCuerpo(plantilla.cuerpo);
        setUsarCuerpoGrafico(false);
      }
      if (plantilla.cuerpoEsGrafico) {
        setCuerpo(undefined);
        setUsarCuerpoGrafico(true);
      }
      setAsunto(plantilla.asunto);
      setGrupoPropietarios(plantilla.grupoPropietarios?.id);
      setPropietariosIndividuales(plantilla.propietariosIndividuales);
      setUsarGruposDePropietarios(!!plantilla.grupoPropietarios);
      setPeriodicidad(plantilla.programacion?.periodicidad);
      setProgramar(!!plantilla.programacion);
      reiniciarFueTocados();
    }
  }, [plantillaIsSuccess, plantilla]);

  if (
    plantillaIsError ||
    errorDescargaAdjunto ||
    errorDescargaCuerpoGrafico ||
    propietariosIsError ||
    editarPlantillaMutation.isError ||
    eliminarPlantillaMutation.isError || 
    enviarMensajeMutation.isError
  ) {
    return <ServerErrorModal />;
  }

  return (
    <>
      {propietariosIsSuccess && (
        <PropietariosModal
          seleccionarPropietario={onSeleccionarPropietario}
          onCerrar={onCerrarPropietariosModal}
          propietarios={propietariosPorSeleccionar || []}
          mostrar={mostrarPropietariosModal}
        />
      )}
      {(plantillaIsLoading || propietariosIsLoading) && <LoadingScreen />}
      <Card className="p-1">
        <Card.Body>
          <Stack gap={2}>
            <section>
              <h5>Diseño de plantilla</h5>
            </section>
            <section>
              <Form.Check
                type="switch"
                id="usar-grupos-propietarios-switch"
                label="Usar grupos de propietarios"
                checked={usarGruposDePropietarios}
                onChange={() => {
                  setUsarGruposDePropietarios((p) => !p);
                  setGrupoPropietarios(undefined);
                  setPropietariosIndividuales([]);
                  setPropietariosIndividualesFueTocado(false);
                }}
              />
            </section>

            <section>
              {usarGruposDePropietarios && (
                <InputGroup className="mb-3">
                  <InputGroup.Text id="para-label">Para:</InputGroup.Text>
                  <Form.Select
                    value={grupoPropietarios}
                    aria-label="para"
                    aria-describedby="para-label"
                    onChange={(v) => setGrupoPropietarios(v.target.value)}
                    onInput={() => setGrupoPropietariosFueTocado(true)}
                  >
                    {(listasClientes || []).map((cli) => (
                      <option key={cli.id} value={cli.id}>
                        {cli.nombre}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
              )}
              {!usarGruposDePropietarios && (
                <div className="mb-3 row">
                  <div className="col col-auto">
                    <Button
                      size="sm"
                      id="boton-agregar-propietario"
                      onClick={onAgregarPropietarioClick}
                    >
                      Agregar propietario
                    </Button>
                  </div>

                  <div className="col col-auto d-flex flex-column gap-1">
                    {(propietariosIndividuales || []).map((p) => (
                      <PropietarioIndividual
                        propietario={p}
                        key={p.id}
                        onEliminar={onEliminarPropietario}
                      />
                    ))}
                  </div>
                </div>
              )}
              <InputGroup className="mb-3">
                <InputGroup.Text id="asunto-label">Asunto:</InputGroup.Text>
                <Form.Control
                  aria-label="asunto"
                  aria-describedby="asunto-label"
                  value={asunto}
                  onChange={(a) => setAsunto(a.target.value)}
                  onInput={() => setAsuntoFueTocado(true)}
                />
              </InputGroup>
              <section>
                <Form.Check
                  type="switch"
                  id="cuerpo-grafico-switch"
                  label="Usar gráfico como cuerpo"
                  checked={usarCuerpoGrafico}
                  onChange={onUsarCuerpoGraficoToggle}
                />
              </section>
              {!usarCuerpoGrafico && (
                <section className="mt-2">
                  <ReactQuill
                    value={cuerpo}
                    onChange={(v) => {
                      setCuerpo(v);
                    }}
                    onKeyUp={() => {
                      if (!cuerpoFueTocado) {
                        setCuerpoFueTocado(true);
                      }
                    }}
                    theme="snow"
                    modules={quillModules}
                  />
                </section>
              )}
              {usarCuerpoGrafico && (
                <section className="mt-2">
                  <Form.Group>
                    <Form.Label>Seleccionar cuerpo:</Form.Label>
                    <Form.Control
                      type="file"
                      aria-label="cuerpoGrafico"
                      onChange={onSeleccionarCuerpo}
                    ></Form.Control>
                  </Form.Group>
                </section>
              )}

              {plantilla?.cuerpoEsGrafico && (
                <section className="mt-2">
                  <Button onClick={onDescargarCuerpoGraficoClick} size="sm">
                    <span>Descargar cuerpo grafico </span>
                    <CloudDownloadIcon sx={{ fontSize: 18 }} />
                  </Button>
                </section>
              )}
              {(plantilla?.tieneAdjunto && !eliminarAdjunto) && (
                <section className="mt-2 d-flex gap-2">
                  <Button onClick={onDescargarAdjuntoClick} size="sm">
                    <span>Descargar adjunto </span>
                    <CloudDownloadIcon sx={{ fontSize: 18 }} />
                  </Button>
                  <Button
                    variant="danger"
                    onClick={onEliminarAdjuntoClick}
                    size="sm"
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </Button>
                </section>
              )}
              <section className="mt-2">
                <Form.Check
                  type="switch"
                  id="programar-switch"
                  label="Programar"
                  checked={programar}
                  onChange={onProgramarToggle}
                />
                {programar && (
                  <section className="mt-2 w-25">
                    <Form.Select
                      value={periodicidad}
                      aria-label="periodicidad"
                      aria-describedby="periodicidad-label"
                      onChange={(v) => {
                        setPeriodicidad(
                          v.target.value as PeriodicidadCorreoMasivo
                        );
                        setPeriodicidadFueTocada(true);
                      }}
                    >
                      {(PERIODICIDADES_CORREOS_MASIVOS || []).map((p) => (
                        <option key={p} value={p}>
                          {labelPorPeriodicidadCorreoMasivo[p]}
                        </option>
                      ))}
                    </Form.Select>
                  </section>
                )}
              </section>
            </section>
            <section className="d-flex gap-2 mt-2">
              <SuccessButton
                size="sm"
                disabled={huboModificaciones}
                onClick={onEnviarClick}
              >
                {mutacionesIsLoading && (
                  <Spinner animation="border" size="sm" />
                )}
                {!mutacionesIsLoading && (
                  <>
                    <span>Enviar</span> <SendIcon sx={{ fontSize: 18 }} />
                  </>
                )}
              </SuccessButton>
              <Button
                size="sm"
                disabled={!huboModificaciones}
                onClick={onGuardarClick}
              >
                {mutacionesIsLoading && (
                  <Spinner animation="border" size="sm" />
                )}
                {!mutacionesIsLoading && (
                  <>
                    <span>Guardar</span> <SaveIcon sx={{ fontSize: 18 }} />
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={onEliminarPlantillaClick}
              >
                {mutacionesIsLoading && (
                  <Spinner animation="border" size="sm" />
                )}
                {!mutacionesIsLoading && (
                  <>
                    <span>Eliminar</span> <DeleteIcon sx={{ fontSize: 18 }} />
                  </>
                )}
              </Button>
            </section>
          </Stack>
        </Card.Body>
      </Card>
    </>
  );
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image"],
    ["clean"],
    [{ color: [] }, { background: [] }],
  ],
};
